/**
 * batch-66 verkoop-extractie (FO-493) - de leerlus, puur. Geen DB, geen klok.
 *
 *   1. `bepaalCorrecties`: het verschil tussen wat het systeem voorstelde en wat
 *      de reviewer bij goedkeuren invulde, per veld.
 *   2. `bepaalCorrectiePatroon`: welke REGELMAAT zit er in de correctie? Staat
 *      de gecorrigeerde waarde in de PDF-tekst bij een label ("Uitgiftedatum"),
 *      of koppelt de reviewer een naam/KvK-nummer telkens aan dezelfde relatie
 *      of BV? Alleen zo'n regelmaat kan later een voorstel worden.
 *   3. `beschrijfVoorstel`: de tekst waarmee het voorstel ter bevestiging wordt
 *      voorgelegd. Het systeem leert nooit stil.
 */

import {
  EXTRACTIE_VELD_LABELS,
  type ExtractieCorrectiePatroon,
  type ExtractieCorrigeerbaarVeld,
  type ExtractieVeld,
  type ExtractieVoorstelPayload,
} from '@streep/types'
import { normalizeBedrijfsnaam, normaliseerKvk } from '../eigen-bv-herkenning'
import { isoNaarNlDatum, normaliseerWitruimte, plet, pletKlein, splitsRegels } from './tekst'

/** De waarden van een concept zoals voorgesteld of zoals ingevoerd (alles als tekst). */
export interface ConceptWaarden {
  klant: string | null
  bv: string | null
  factuurnummer: string | null
  /** jjjj-mm-dd */
  factuurdatum: string | null
  vervaldatum: string | null
  /** Bedragen als decimale tekst met twee decimalen ("3259.56"). */
  subtotaal: string | null
  btwBedrag: string | null
  btwPercentage: string | null
  referentienummer: string | null
  omschrijving: string | null
}

export interface VeldCorrectie {
  veld: ExtractieCorrigeerbaarVeld
  extractieWaarde: string | null
  correctieWaarde: string | null
}

const BEDRAG_VELDEN: readonly ExtractieCorrigeerbaarVeld[] = ['subtotaal', 'btwBedrag', 'btwPercentage']

function leeg(waarde: string | null): boolean {
  return waarde === null || waarde.trim().length === 0
}

function gelijk(veld: ExtractieCorrigeerbaarVeld, a: string | null, b: string | null): boolean {
  if (leeg(a) && leeg(b)) return true
  if (leeg(a) || leeg(b)) return false
  const x = (a ?? '').trim()
  const y = (b ?? '').trim()
  if (BEDRAG_VELDEN.includes(veld)) {
    const nx = Number.parseFloat(x)
    const ny = Number.parseFloat(y)
    if (Number.isFinite(nx) && Number.isFinite(ny)) return Math.round(nx * 100) === Math.round(ny * 100)
  }
  if (veld === 'omschrijving') return normaliseerWitruimte(x) === normaliseerWitruimte(y)
  return x === y
}

/** De velden waar de reviewer iets anders invulde dan het systeem voorstelde. */
export function bepaalCorrecties(voorgesteld: ConceptWaarden, ingevoerd: ConceptWaarden): VeldCorrectie[] {
  const velden: ExtractieCorrigeerbaarVeld[] = [
    'klant',
    'bv',
    'factuurnummer',
    'factuurdatum',
    'vervaldatum',
    'subtotaal',
    'btwBedrag',
    'btwPercentage',
    'referentienummer',
    'omschrijving',
  ]
  const uit: VeldCorrectie[] = []
  for (const veld of velden) {
    if (gelijk(veld, voorgesteld[veld], ingevoerd[veld])) continue
    uit.push({
      veld,
      extractieWaarde: leeg(voorgesteld[veld]) ? null : (voorgesteld[veld] ?? '').trim(),
      correctieWaarde: leeg(ingevoerd[veld]) ? null : (ingevoerd[veld] ?? '').trim(),
    })
  }
  return uit
}

// ─── Label bij een waarde in de PDF-tekst ────────────────────────────────────

/** De schrijfwijzen waarin een waarde in de PDF-tekst kan staan (geplet). */
function waardeVarianten(veld: ExtractieCorrigeerbaarVeld, waarde: string): string[] {
  const kaal = plet(waarde)
  if (veld === 'factuurdatum' || veld === 'vervaldatum') {
    const nl = isoNaarNlDatum(kaal)
    if (nl === kaal) return [kaal]
    return [nl, nl.replace(/-/g, '/'), nl.replace(/-/g, '.'), kaal]
  }
  if (veld === 'subtotaal' || veld === 'btwBedrag') {
    const n = Number.parseFloat(kaal)
    if (!Number.isFinite(n)) return []
    const metDuizendtal = new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n)
    const zonder = metDuizendtal.replace(/\./g, '')
    return metDuizendtal === zonder ? [metDuizendtal] : [metDuizendtal, zonder]
  }
  return kaal.length >= 2 ? [kaal] : []
}

export interface LabelTreffer {
  /** Vergelijk-sleutel van het label (geplet, kleine letters, alleen letters en cijfers). */
  sleutel: string
  /** Het label zoals het in de tekst staat (witruimte samengevoegd). */
  label: string
}

/**
 * Zoek het label dat in de PDF-tekst direct VOOR de waarde staat, op dezelfde
 * regel ("Uitgifte datum : 08- 09- 2026" -> label "Uitgifte datum"). Alleen een
 * label van letters (met eventueel # . -) telt; een regel met bedragen ervoor
 * is geen label. Staat de waarde bij meerdere labels, dan wint het eerste.
 */
export function vindLabelBijWaarde(
  tekst: string | null,
  veld: ExtractieCorrigeerbaarVeld,
  waarde: string | null,
): LabelTreffer | null {
  if (!tekst || waarde === null) return null
  const varianten = waardeVarianten(veld, waarde).map((v) => v.toLowerCase())
  if (varianten.length === 0) return null
  for (const regel of splitsRegels(tekst.slice(0, 200_000))) {
    const klein = pletKlein(regel)
    for (const variant of varianten) {
      const op = klein.indexOf(variant)
      if (op <= 0) continue
      // Alleen letters (en # . -) voor de waarde, afgesloten door ":" of "#" of een euroteken.
      const voor = klein.slice(0, op)
      const m = /^([a-z][a-z#.\-]{1,39}?)[:#]?€?$/.exec(voor.normalize('NFD').replace(/\p{M}/gu, ''))
      if (!m) continue
      const sleutel = (m[1] ?? '').replace(/[^a-z0-9]/g, '')
      if (sleutel.length < 2) continue
      // Het leesbare label: de ruwe regel tot aan de eerste dubbele punt of het eerste cijfer.
      const ruw = /^([^:€\d]+)/.exec(regel)?.[1] ?? ''
      const label = normaliseerWitruimte(ruw).replace(/[\s#]+$/, '')
      return { sleutel, label: label.length > 0 ? label : (m[1] ?? '') }
    }
  }
  return null
}

// ─── Patroon ─────────────────────────────────────────────────────────────────

export interface CorrectiePatroonUitkomst {
  soort: ExtractieCorrectiePatroon
  /** Sleutel waarop gelijke correcties geteld worden (zelfde profiel + veld + sleutel). */
  sleutel: string
  /** Wat een voorstel op dit patroon zou doen; null bij `GEEN`. */
  payload: ExtractieVoorstelPayload | null
}

export interface CorrectieContext {
  /** De PDF-tekst van het concept (null = niet beschikbaar). */
  pdfTekst: string | null
  /** Wat er op de factuur stond (ruwe extractie). */
  debiteurnaam: string | null
  debiteurKvk: string | null
  bvNaam: string | null
  /** De gekozen relatie/BV bij een correctie op klant of bv. */
  gekozenKlant?: { id: string; naam: string } | null
  gekozenBv?: { id: string; naam: string } | null
}

const GEEN = (veld: ExtractieVeld): CorrectiePatroonUitkomst => ({
  soort: 'GEEN',
  sleutel: `GEEN|${veld}`,
  payload: null,
})

/** Welke regelmaat zit er in deze correctie (zie de module-doc)? */
export function bepaalCorrectiePatroon(correctie: VeldCorrectie, context: CorrectieContext): CorrectiePatroonUitkomst {
  const { veld } = correctie
  if (veld === 'klant') {
    const klant = context.gekozenKlant
    if (!klant) return GEEN(veld)
    const kvk = normaliseerKvk(context.debiteurKvk)
    if (kvk) {
      return {
        soort: 'KLANT_KVK',
        sleutel: `KLANT_KVK|${kvk}|${klant.id}`,
        payload: { soort: 'KLANT_KVK_ALIAS', kvk, clientId: klant.id, clientNaam: klant.naam },
      }
    }
    const naam = context.debiteurnaam ? normaliseerWitruimte(context.debiteurnaam) : ''
    const sleutel = naam ? normalizeBedrijfsnaam(naam) : ''
    if (sleutel.length >= 3) {
      return {
        soort: 'KLANT_NAAM',
        sleutel: `KLANT_NAAM|${sleutel}|${klant.id}`,
        payload: { soort: 'KLANT_NAAM_ALIAS', naam, clientId: klant.id, clientNaam: klant.naam },
      }
    }
    return GEEN(veld)
  }
  if (veld === 'bv') {
    const bv = context.gekozenBv
    const naam = context.bvNaam ? normaliseerWitruimte(context.bvNaam) : ''
    const sleutel = naam ? normalizeBedrijfsnaam(naam) : ''
    if (!bv || sleutel.length < 3) return GEEN(veld)
    // De naam IS al de naam van de gekozen BV: dan valt er niets te leren.
    if (normalizeBedrijfsnaam(bv.naam) === sleutel) return GEEN(veld)
    return {
      soort: 'BV_NAAM',
      sleutel: `BV_NAAM|${sleutel}|${bv.id}`,
      payload: { soort: 'BV_ALIAS', naam, entityId: bv.id, entityNaam: bv.naam },
    }
  }
  if (veld === 'omschrijving' || veld === 'btwPercentage') return GEEN(veld)

  const treffer = vindLabelBijWaarde(context.pdfTekst, veld, correctie.correctieWaarde)
  if (!treffer) return GEEN(veld)
  return {
    soort: 'LABEL',
    sleutel: `LABEL|${veld}|${treffer.sleutel}`,
    payload: { soort: 'PROFIEL_AANWIJZING', veld, label: treffer.label },
  }
}

// ─── Voorstel ────────────────────────────────────────────────────────────────

/** De aanwijzing die bij het bevestigen van een label-voorstel op het profiel komt. */
export function aanwijzingVoorLabel(veld: ExtractieVeld, label: string): string {
  return `${EXTRACTIE_VELD_LABELS[veld]}: gebruik de waarde die op de factuur bij "${label}" staat (ook als de spaties in de tekst anders staan).`
}

/** Leesbare uitleg van een voorstel: wat is er herhaald gecorrigeerd en wat doet bevestigen. */
export function beschrijfVoorstel(
  payload: ExtractieVoorstelPayload,
  aantal: number,
  profielNaam: string | null,
): string {
  const waar = profielNaam ? `op het profiel "${profielNaam}"` : 'bij facturen zonder opmaakprofiel'
  const keer = `${aantal} keer`
  switch (payload.soort) {
    case 'PROFIEL_AANWIJZING':
      return `Het veld ${EXTRACTIE_VELD_LABELS[payload.veld]} is ${keer} ${waar} gecorrigeerd naar de waarde bij "${payload.label}" op de factuur. Bevestigen voegt die aanwijzing toe, zodat de extractie voortaan die waarde neemt.`
    case 'KLANT_KVK_ALIAS':
      return `Een factuur met KvK-nummer ${payload.kvk} is ${keer} handmatig aan relatie "${payload.clientNaam}" gekoppeld. Bevestigen legt dat KvK-nummer vast als herkenning voor deze relatie.`
    case 'KLANT_NAAM_ALIAS':
      return `Een factuur op naam van "${payload.naam}" is ${keer} handmatig aan relatie "${payload.clientNaam}" gekoppeld. Bevestigen legt die naam vast als herkenning voor deze relatie.`
    case 'BV_ALIAS':
      return `Een factuur met de afzendernaam "${payload.naam}" is ${keer} handmatig aan BV "${payload.entityNaam}" gekoppeld. Bevestigen voegt die naam toe als alias van de BV.`
    case 'FACTORING_IBAN':
      return `In de betalingsinstructies van een factuur van "${payload.entityNaam}" staat het rekeningnummer ${payload.iban}${payload.tenNameVan ? ` ten name van ${payload.tenNameVan}` : ''}. Bevestigen legt dit vast als factoring-IBAN: het wordt dan nooit als rekeningnummer van een klant of BV overgenomen.`
    default: {
      const nooit: never = payload
      return nooit
    }
  }
}

/** Moet er nu een voorstel komen? (Nooit een tweede voor hetzelfde patroon.) */
export function moetVoorstelMaken(args: {
  /** Gelijke correcties die nog niet door een eerder besluit zijn afgedekt. */
  aantalGelijke: number
  drempel: number
  /** Er staat al een open voorstel, of het patroon is al bevestigd. */
  alAfgedekt: boolean
}): boolean {
  return !args.alAfgedekt && args.aantalGelijke >= Math.max(2, args.drempel)
}
